import React, { useEffect } from 'react';
import { Box, Container, Typography, Paper, Button, Grid, Card, CardContent, Avatar } from '@mui/material';
import { CheckCircle, Email, LinkedIn, Instagram, PersonalVideo } from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import StripeCheckoutButton from '../components/StripeCheckoutButton';

const CALENDAR_BOOKING_URL = 'https://calendar.app.google/JnJfYuVfLLnjqzwT9';
const SANDBOX_PERSONAL_PLAN_PRICE_ID = 'price_1TjTXYAS9lDGoaELPj147EP8';
const LIVE_PERSONAL_PLAN_PRICE_ID = 'price_1TjTY4AFlZQpjEwRBxERqzwX';

const getDefaultPersonalPlanPriceId = () => (
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.startsWith('pk_live_')
    ? LIVE_PERSONAL_PLAN_PRICE_ID
    : SANDBOX_PERSONAL_PLAN_PRICE_ID
);

const SuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const product = searchParams.get('product');
  const personalPlanPriceId = process.env.REACT_APP_PERSONAL_PLAN_PRICE_ID || getDefaultPersonalPlanPriceId();

  const upsellCancelParams = new URLSearchParams();
  if (sessionId) {
    upsellCancelParams.set('session_id', sessionId);
  }
  if (product) {
    upsellCancelParams.set('product', product);
  }
  const upsellCancelUrl = `/success${upsellCancelParams.toString() ? `?${upsellCancelParams.toString()}` : ''}`;

  // Set page title and redirect if required parameters are missing
  useEffect(() => {
    document.title = 'Payment Successful - Thank You! | Ray Jones';

    if (!product) {
      console.warn('Missing product parameter for SuccessPage');
      navigate('/');
    }
  }, [product, navigate]);

  // Video testimonials from coaching page
  const testimonials = [
    {
      name: "Andrew",
      company: "C3.ai",
      salary: "$255k",
      quote:
        "I got an offer at C3AI, Raymond helped me a ton with interviewing and preparing for interviews. This would have taken a lot longer if I didn't have his help.",
      avatar: "A",
      loomUrl:
        "https://www.loom.com/embed/32f365aebbc4426b8cd098535f54f1ac?sid=cef9de69-edee-4889-a344-6f88a5e44e7e&hideEmbedTopBar=true&hide_owner=true&hide_share=true&hide_title=true",
    },
    {
      name: "Om",
      company: "Google",
      salary: "$180k",
      quote:
        "After working with Raymond, I passed Google's onsite interview with confidence. His mock interviews prepared me for every scenario.",
      avatar: "O",
      loomUrl:
        "https://www.loom.com/embed/c8ba8a64b5864dc79d1e6520af3f1ee3?sid=aec93f14-b44a-473c-91f3-ad701cff191b&hideEmbedTopBar=true&hide_owner=true&hide_share=true&hide_title=true",
    },
    {
      name: "TJ",
      company: "Fortune 500",
      salary: "$179k",
      quote:
        "He's been instrumental in my process getting a ton of interviews, getting a ton of on-sites, and ultimately landing a job offer where I'm making considerably more than my current role.",
      avatar: "T",
      loomUrl:
        "https://www.loom.com/embed/83e83110c4634fe0b62dd351d8b758f6?sid=a668ef97-5408-46c5-9679-b3fda3126569&hideEmbedTopBar=true&hide_owner=true&hide_share=true&hide_title=true",
    },
    {
      name: "Matt",
      company: "Affirm",
      salary: "$160k",
      quote:
        "The time I spent with Raymond was crucial for my interviews, especially the behavioral stuff. He kept me accountable and really sped up the process.",
      avatar: "M",
      loomUrl:
        "https://www.loom.com/embed/0ef7eea40968445ea612570737a3de11?sid=1f40c316-6be3-4386-91df-78d25a714539&hideEmbedTopBar=true&hide_owner=true&hide_share=true&hide_title=true",
    },
    {
      name: "Chris",
      company: "",
      salary: "",
      quote:
        "This kind of practice one-on-one really helps you cement down the patterns you need to actually do well in the interview. I definitely recommend it to everybody.",
      avatar: "C",
      loomUrl:
        "https://www.loom.com/embed/b8126cb7c64b48799b6b81d3d5c967b3?sid=9d4749f2-c59c-42ec-bf19-0abe5c01c3e3&hideEmbedTopBar=true&hide_owner=true&hide_share=true&hide_title=true",
    },
    {
      name: "Hassan",
      company: "",
      salary: "",
      quote:
        "Mock interviews have been really helpful. I feel that Ray has been simulating the real-time interviews that happen at these big tech companies.",
      avatar: "H",
      loomUrl:
        "https://www.loom.com/embed/d6fcb15c2f32452da7cd4ff9201df1ff?sid=dc6a6798-0daf-4319-b3df-0d6f506230a9&hideEmbedTopBar=true&hide_owner=true&hide_share=true&hide_title=true",
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Box sx={{ pt: 12, pb: 8 }}>
        <Container maxWidth="lg">
          {/* Payment Success Confirmation */}
          <Paper
            sx={{
              p: 6,
              bgcolor: 'rgba(0, 255, 0, 0.1)',
              border: '3px solid #00FF00',
              borderRadius: 4,
              textAlign: 'center',
              mb: 6
            }}
          >
            <CheckCircle
              sx={{
                fontSize: 80,
                color: '#00FF00',
                mb: 3
              }}
            />
            <Typography
              variant="h3"
              gutterBottom
              sx={{ color: 'text.primary', mb: 3, fontWeight: 'bold' }}
            >
              Payment Successful! 🎉
            </Typography>
            
            <Typography
              variant="h6"
              sx={{ color: 'text.primary', mb: 4, lineHeight: 1.5 }}
            >
              Check your email for the complete resource bundle including the LeetCode Roadmap
            </Typography>
          
          </Paper>

          {/* VSL Video Section */}
          <Paper
            sx={{
              p: 4,
              bgcolor: 'background.paper',
              borderRadius: 4,
              mb: 6,
              textAlign: 'center'
            }}
          >
            <Typography
              variant="h4"
              sx={{ color: 'secondary.main', mb: 4, fontWeight: 'bold' }}
            >
              Wait. Before you go, checkout out this 1 minute video
            </Typography>
            
            <Box
              sx={{
                position: 'relative',
                paddingBottom: '56.25%',
                height: 0,
                overflow: 'hidden',
                borderRadius: 2,
                mb: 4
              }}
            >
              <iframe
                src="https://www.loom.com/embed/80d49f4b5fdf429b9b5cf88c911e537a?hide_owner=true&hide_share=true&hide_title=true&hideEmbedTopBar=true"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                allowFullScreen
                title="Upsell Video"
              />
            </Box>
          </Paper>

          {/* Upsell Options */}
          <Grid container spacing={4} sx={{ mb: 6 }}>
            {/* First Upsell - $99 Personalized Plan */}
            <Grid size={{ xs: 12, md: 12 }}>
              <Card
                sx={{
                  bgcolor: 'background.paper',
                  border: '3px solid',
                  borderColor: 'secondary.main',
                  borderRadius: 4,
                  height: '100%',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'translateY(-8px)' }
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <PersonalVideo sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
                  <Typography variant="h4" sx={{ color: 'secondary.main', mb: 2, fontWeight: 'bold' }}>
                    1-on-1 Personalized Plan
                  </Typography>
                  <Typography variant="h5" sx={{ color: 'text.primary', mb: 3 }}>
                    $99
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.6 }}>
                    Work personally with me to get your exact homework and personalized improvement plan. 
                    I'll analyze your background and create a custom roadmap just for you.
                  </Typography>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      ✅ Personal 1-on-1 consultation<br/>
                      ✅ Custom homework assignments<br/>
                      ✅ Personalized improvement plan<br/>
                      ✅ Direct access to Ray Jones
                    </Typography>
                  </Box>
                  <StripeCheckoutButton
                    priceId={personalPlanPriceId}
                    buttonText="Get My Personal Plan - $99"
                    variant="contained"
                    color="secondary"
                    size="large"
                    successUrl={CALENDAR_BOOKING_URL}
                    cancelUrl={upsellCancelUrl}
                    sx={{
                      color: 'black',
                      py: 2,
                      width: '100%',
                      fontWeight: 'bold',
                      fontSize: '1.1rem',
                      '&:hover': { transform: 'scale(1.02)' },
                      transition: 'transform 0.2s'
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>


          {/* Testimonials Section */}
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{ color: 'text.primary', mb: 4 }}
            >
              Hear From People I've <span style={{ color: '#88C0D0' }}>Helped</span>
            </Typography>

            <Grid container spacing={3}>
              {testimonials.map((testimonial, index) => (
                <Grid key={index} size={{ xs: 12, md: 4 }}>
                  <Card
                    sx={{
                      bgcolor: 'background.paper',
                      height: '100%',
                      border: '2px solid #434C5E',
                      '&:hover': {
                        border: '2px solid #88C0D0',
                        transform: 'translateY(-4px)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {testimonial.loomUrl && (
                      <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                        <iframe
                          src={testimonial.loomUrl}
                          frameBorder="0"
                          allowFullScreen
                          loading="lazy"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                          }}
                          title={`${testimonial.name} Testimonial`}
                        />
                      </Box>
                    )}
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar
                          sx={{ bgcolor: 'secondary.main', color: 'black', mr: 2 }}
                        >
                          {testimonial.avatar}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ color: 'text.primary' }}>
                            {testimonial.name}
                          </Typography>
                          {(testimonial.company || testimonial.salary) && (
                            <Typography
                              variant="body2"
                              sx={{ color: 'secondary.main', fontWeight: 'bold' }}
                            >
                              {testimonial.company}{testimonial.company && testimonial.salary && ' • '}{testimonial.salary}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', lineHeight: 1.6 }}
                      >
                        "{testimonial.quote}"
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Contact Information */}
          <Paper
            sx={{
              p: 4,
              bgcolor: 'background.paper',
              borderRadius: 4,
              textAlign: 'center',
              mb: 4
            }}
          >
            <Typography variant="h5" sx={{ color: 'secondary.main', mb: 3, fontWeight: 'bold' }}>
              Questions? Contact Me:
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              <Grid size="auto">
                <Button
                  variant="outlined"
                  startIcon={<LinkedIn />}
                  href="https://www.linkedin.com/in/therealraymondjones/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'secondary.main',
                    borderColor: 'secondary.main',
                    '&:hover': {
                      bgcolor: 'secondary.main',
                      color: 'black'
                    }
                  }}
                >
                  LinkedIn
                </Button>
              </Grid>
              <Grid size="auto">
                <Button
                  variant="outlined"
                  startIcon={<Email />}
                  href="mailto:raymond@raymondjones.dev"
                  sx={{
                    color: 'secondary.main',
                    borderColor: 'secondary.main',
                    '&:hover': {
                      bgcolor: 'secondary.main',
                      color: 'black'
                    }
                  }}
                >
                  Email
                </Button>
              </Grid>
              <Grid size="auto">
                <Button
                  variant="outlined"
                  startIcon={<Instagram />}
                  href="https://www.instagram.com/realraymondjones"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'secondary.main',
                    borderColor: 'secondary.main',
                    '&:hover': {
                      bgcolor: 'secondary.main',
                      color: 'black'
                    }
                  }}
                >
                  Instagram
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {sessionId && (
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 4 }}>
              Order ID: {sessionId}
            </Typography>
          )}
        </Container>
      </Box>
    </Box>
  );
};

export default SuccessPage;
